import type { ReactElement, ReactNode } from "react"

/**
 * The theme toggle's two faces. Same 24-unit viewBox, stroke weight, and
 * `currentColor` as `Gift` and `BookLink`, so the control reads as part of the
 * same icon family.
 *
 * Decorative: the button around them carries the accessible name, which has to
 * name the action ("Switch to dark theme") rather than describe the glyph.
 */

interface IconProps {
  readonly size?: number
}

/** One stroked glyph on the family's shared frame. */
const Glyph = ({ size = 20, children }: IconProps & { readonly children: ReactNode }): ReactElement => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
)

/** Shown in light mode, where pressing the button turns the lights off. */
export const Moon = ({ size }: IconProps): ReactElement => (
  <Glyph size={size}>
    <path d="M20.5 14.6A8.5 8.5 0 1 1 9.4 3.5a7 7 0 0 0 11.1 11.1z" />
  </Glyph>
)

/** Shown in dark mode. Eight rays, because four reads as a compass rose at 20px. */
export const Sun = ({ size }: IconProps): ReactElement => (
  <Glyph size={size}>
    <circle cx="12" cy="12" r="4.25" />
    <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4" />
  </Glyph>
)
